import * as RadixProgress from '@radix-ui/react-progress';
import { cx, css, cva } from '../styled-system/css';
import { SystemStyleObject } from '../styled-system/types';

/**
 * Hat tip: https://css-tricks.com/svg-line-animation-works/
 */

const progress = cva({
  base: {
    overflow: 'hidden',
  },
  defaultVariants: { type: 'linear' },
  variants: {
    type: {
      linear: {
        width: '100%',
        height: '4px',
        background: 'm3.surfaceContainerHighest',
      },
      circular: {
        width: '100%',
        height: '100%',
      },
    },
  },
});

const indicator = cva({
  base: { width: '100%', height: '100%' },
  defaultVariants: { determinant: 'determinant', type: 'linear' },
  variants: {
    determinant: {
      determinant: {},
      indeterminant: {
        animation: 'linearProgress 10s infinite',
      },
    },
    type: {
      linear: {
        background: 'm3.primary',
        transition: 'transform 660ms cubic-bezier(0.65, 0, 0.35, 1)',
      },
      circular: {
        color: 'm3.primary',
        animation: 'none',
      },
    },
  },
});

type ProgressType = NonNullable<Parameters<typeof progress>[0]>['type'];
type Css = { css?: SystemStyleObject };

export type ProgressProps = { type?: ProgressType } & RadixProgress.ProgressProps & Css;
export type ProgressIndicatorProps = {
  type?: ProgressType;
  value: ProgressProps['value'];
} & RadixProgress.ProgressIndicatorProps &
  Css;

function Root({ className, css: cssObject = {}, type, ...props }: ProgressProps) {
  return <RadixProgress.Root className={cx(className, progress({ type }), css({ ...cssObject }))} {...props} />;
}

function Indicator({ className, css: cssObject = {}, type, value, ...props }: ProgressIndicatorProps) {
  const normalizedValue = value ?? 0;
  const determinant = typeof value === 'number' ? 'determinant' : 'indeterminant';
  const isDeterminant = determinant === 'determinant';
  const isLinear = type === 'linear';

  return (
    <RadixProgress.Indicator
      className={cx(className, indicator({ determinant, type }), css({ ...cssObject }))}
      style={isDeterminant && isLinear ? { transform: `translateX(-${100 - normalizedValue}%)` } : {}}
      {...props}
    >
      {type === 'circular' && (
        <svg
          style={{ transformBox: 'fill-box', transformOrigin: 'center', transform: 'rotate(-90deg)' }}
          viewBox='0 0 100 100'
          xmlns='http://www.w3.org/2000/svg'
        >
          <circle
            cx='50%'
            cy='50'
            fill='transparent'
            r='44'
            stroke='currentColor'
            strokeDasharray='277'
            strokeDashoffset={`${277 - (normalizedValue / 100) * 277}`}
            strokeWidth='10'
            style={isDeterminant ? {} : { animation: 'dashProgress 5s infinite' }}
          />
          <animate attributeName='stroke-dashoffset' id='stroke-dashoffset' />
        </svg>
      )}
    </RadixProgress.Indicator>
  );
}

export const Progress = {
  Root,
  Indicator,
};
